// /vendor/jobs/:assignmentId — per-assignment detail.
//
// Reads vendor_jobs (security_invoker + explicit vendor_id filter) with
// maybeSingle: a row outside the caller's scope simply does not return,
// which becomes the empty-state "Not found." page. The empty state IS
// security — no separate authorization check in the UI.
//
// Surfaces:
//   * Full assignment card: title, schedule, address with maps link,
//     job_notes, WO/PO, budget, status timeline (assigned → responded
//     → completed)
//   * Photos the staff attached (job-scope) — RLS chains scope:
//     field_job_photos.vendor_read returns rows IFF caller has an
//     accepted/pending assignment for that field_job_id, so the
//     existing PortalPhotoStrip read pattern (listJobPhotos +
//     getJobPhotoSignedUrl) works unchanged.
//   * Accept/Decline buttons when status='pending' (session ops)
//   * Download Work Order button (vendor-work-order session op)
//   * Proof-of-work upload + Mark complete (status='accepted')

import { useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  ArrowLeft, MapPin, CalendarClock, DollarSign, FileText, Download, CheckCircle2,
  XCircle, Camera, Loader2, Image as ImageIcon, AlertCircle, Truck, MapPinned,
  TimerReset, RotateCcw,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { toast } from 'sonner'
import { formatRelativeDate } from '@/lib/utils'
import { mapsUrl } from '@/lib/address'
import { listJobPhotos, getJobPhotoSignedUrl, type JobPhoto } from '@/lib/jobPhotos'
import { uploadVendorJobPhoto } from '@/lib/vendorJobPhotos'
import { transcodeImageToWebP } from '@/lib/imageTranscode'
import { Lightbox } from '@/components/JobPhotoStrip'

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vendor-respond`
const WORK_ORDER_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vendor-work-order`
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string

const DECLINE_MAX = 2000
const NOTE_MAX = 2000

// Single source of truth for per-lifecycle visual tokens + button labels.
// Chip colors live here so the list page (VendorJobs) can pull the same
// map for its latest-state chip.
export const EVENT_COPY: Record<LifecycleEvent, {
  label: string; chipLabel: string; toast: string; icon: typeof Truck;
  bg: string; border: string; color: string;
}> = {
  en_route:               { label: "I'm on my way",        chipLabel: 'En route',     toast: 'On the way',          icon: Truck,        bg: 'var(--info-dim)',     border: 'var(--info-border)',     color: 'var(--info)' },
  on_site:                { label: 'Arrived on site',      chipLabel: 'On site',      toast: 'Arrival recorded',    icon: MapPinned,    bg: 'var(--info-dim)',     border: 'var(--info-border)',     color: 'var(--info)' },
  delayed:                { label: 'Report a delay',       chipLabel: 'Delayed',      toast: 'Delay reported',      icon: TimerReset,   bg: 'var(--warning-dim)',  border: 'var(--warning-border)',  color: 'var(--warning)' },
  return_visit_scheduled: { label: 'Schedule return visit', chipLabel: 'Return visit', toast: 'Return visit logged', icon: RotateCcw,    bg: 'var(--accent-dim)',   border: 'var(--accent-border)',   color: 'var(--accent-text)' },
  completed:              { label: 'Mark complete',        chipLabel: 'Complete',     toast: 'Marked complete',     icon: CheckCircle2, bg: 'var(--positive-dim)', border: 'var(--positive-border)', color: 'var(--positive)' },
}

type LifecycleEvent = 'en_route' | 'on_site' | 'delayed' | 'return_visit_scheduled' | 'completed'

interface AssignmentEvent {
  id: string
  assignment_id: string
  vendor_user_id: string | null
  event_type: LifecycleEvent
  note: string | null
  scheduled_return_date: string | null
  created_at: string
}

interface VendorJobRow {
  assignment_id: string
  // assignment_status is NULL on self-entered rows (no assignment exists).
  // The `source` field below is the SOLE discriminator — see the Phase 0
  // design doc. A null assignment_status is a structural marker, not a
  // sentinel.
  assignment_status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled' | null
  budget_offered: number | string | null
  assigned_at: string
  responded_at: string | null
  token_expires_at: string | null
  decline_reason: string | null
  completed_at: string | null
  latest_event_type: LifecycleEvent | null
  latest_event_at: string | null
  vendor_id: string
  workspace_id: string
  // NULL on self-entered (no underlying field_job). Photos query gates on
  // truthiness; for self-entered the query is naturally disabled.
  field_job_id: string | null
  title: string | null
  job_status: string | null
  scheduled_date: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  address_line1: string | null
  address_line2: string | null
  address_city: string | null
  address_state: string | null
  address_zip: string | null
  job_notes: string | null
  work_order_number: string | null
  purchase_order_number: string | null
  source: 'assigned' | 'self_entered'
}

function formatBudget(b: number | string | null): string | null {
  if (b == null) return null
  const n = typeof b === 'string' ? parseFloat(b) : b
  if (!Number.isFinite(n)) return null
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) }
  catch { return `$${n.toFixed(2)}` }
}

export default function VendorJobDetail() {
  const { assignmentId } = useParams<{ assignmentId: string }>()
  const queryClient = useQueryClient()
  const { vendorUser } = useAuth()
  const [declineOpen, setDeclineOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [uploading, setUploading] = useState(false)
  const [delayOpen, setDelayOpen] = useState(false)
  const [delayNote, setDelayNote] = useState('')
  const [returnOpen, setReturnOpen] = useState(false)
  const [returnDate, setReturnDate] = useState('')
  const [returnNote, setReturnNote] = useState('')
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false)
  // In-place photo lightbox — null = closed, number = open at that index
  // in the combined [...staffPhotos, ...vendorPhotos] array. Replaces
  // the prior <a target="_blank"> that tab-jacked on mobile Safari.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const { data: job, isLoading } = useQuery<VendorJobRow | null>({
    queryKey: ['vendor-job', assignmentId],
    enabled: !!assignmentId,
    queryFn: async () => {
      const { data } = await supabase
        .from('vendor_jobs').select('*')
        .eq('assignment_id', assignmentId!)
        .maybeSingle()
      return (data as VendorJobRow) ?? null
    },
  })

  const { data: photos = [] } = useQuery<JobPhoto[]>({
    queryKey: ['vendor-job-photos', job?.field_job_id],
    enabled: !!job?.field_job_id,
    queryFn: async () => {
      // enabled gate above guarantees field_job_id is non-null here.
      // Self-entered rows have field_job_id=NULL → query never fires.
      const { data, error } = await listJobPhotos(job!.field_job_id!)
      if (error) throw error
      return data ?? []
    },
  })

  // Lifecycle timeline — newest first. Same RLS-scoped table that backs
  // the latest_event_type/_at columns on vendor_jobs.
  const { data: events = [] } = useQuery<AssignmentEvent[]>({
    queryKey: ['vendor-job-events', assignmentId],
    enabled: !!assignmentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_assignment_events')
        .select('id, assignment_id, vendor_user_id, event_type, note, scheduled_return_date, created_at')
        .eq('assignment_id', assignmentId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as AssignmentEvent[]
    },
  })

  const directionsUrl = useMemo(() => job ? mapsUrl({
    line1: job.address_line1, line2: job.address_line2,
    city: job.address_city, state: job.address_state, zip: job.address_zip,
  }) : null, [job])

  // ─── Session ops mutation (accept / decline) ──────────────────────
  const respond = useMutation({
    mutationFn: async ({ op, reason }: { op: 'accept_session' | 'decline_session'; reason?: string | null }) => {
      const session = await supabase.auth.getSession()
      const accessToken = session.data.session?.access_token
      if (!accessToken) throw new Error('Not signed in')
      const resp = await fetch(FUNCTIONS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}`, 'apikey': ANON_KEY },
        body: JSON.stringify({ op, assignment_id: assignmentId, declineReason: reason ?? undefined }),
      })
      const body = await resp.json().catch(() => ({}))
      if (resp.status === 200 && body?.ok) return { kind: 'ok' as const, status: body.status as 'accepted' | 'declined' }
      if (resp.status === 410) return { kind: 'raced' as const, status: body?.status as string }
      throw new Error(body?.error ?? `Request failed (${resp.status})`)
    },
    onSuccess: (res) => {
      if (res.kind === 'ok') toast.success(res.status === 'accepted' ? 'Accepted' : 'Declined')
      else toast.warning(`Already handled — was ${res.status}`)
      queryClient.invalidateQueries({ queryKey: ['vendor-job', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['vendor-jobs'] })
      setDeclineOpen(false); setDeclineReason('')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Something went wrong'),
  })

  // ─── Lifecycle: append a vendor_assignment_event ─────────────────
  // V2c moved the canonical complete action to an events INSERT (see
  // 20260607140000_vendor_assignment_events.sql — the V2b
  // vendor_assignment_completed_notify trigger was DROPPED). The
  // single mutation below covers all five lifecycle actions; the
  // BEFORE-INSERT trigger validates per-type required fields (delay
  // reason, return date) and 2000-char-truncates the note, the
  // AFTER-INSERT trigger stamps completed_at on completed events and
  // fires bell notifications for delayed / return_visit_scheduled /
  // completed.
  const logEvent = useMutation({
    mutationFn: async (vars: { type: LifecycleEvent; note?: string; date?: string }) => {
      if (!vendorUser) throw new Error('Not signed in')
      const { error } = await supabase
        .from('vendor_assignment_events')
        .insert({
          assignment_id: assignmentId!,
          vendor_user_id: vendorUser.id,
          event_type: vars.type,
          note: vars.note ?? null,
          scheduled_return_date: vars.date ?? null,
        })
      if (error) throw error
    },
    onSuccess: (_d, vars) => {
      toast.success(EVENT_COPY[vars.type].toast)
      queryClient.invalidateQueries({ queryKey: ['vendor-job', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['vendor-job-events', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['vendor-jobs'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not record update'),
  })

  // ─── Work order download via session op ───────────────────────────
  const downloadWorkOrder = async () => {
    if (!assignmentId) return
    const session = await supabase.auth.getSession()
    const accessToken = session.data.session?.access_token
    if (!accessToken) { toast.error('Not signed in'); return }
    const resp = await fetch(WORK_ORDER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}`, 'apikey': ANON_KEY },
      body: JSON.stringify({ op: 'download_session', assignment_id: assignmentId }),
    })
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}))
      toast.error(body?.error ?? `Download failed (${resp.status})`)
      return
    }
    const blob = await resp.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `WO-${job?.work_order_number ?? assignmentId}.pdf`
    document.body.appendChild(a); a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // ─── Upload completion photos ─────────────────────────────────────
  const onUpload = async (files: FileList) => {
    if (!job || !vendorUser || !job.field_job_id) return
    setUploading(true)
    let okCount = 0
    let errMsg: string | null = null
    for (const file of Array.from(files)) {
      // Best-effort client-side WebP transcode (capacity audit §4).
      // Helper never throws — returns the original file on any failure.
      const optimized = await transcodeImageToWebP(file)
      const { error } = await uploadVendorJobPhoto({
        workspaceId: job.workspace_id,
        jobId: job.field_job_id,
        vendorUserId: vendorUser.id,
        file: optimized,
      })
      if (error) { errMsg = error.message; break }
      okCount += 1
    }
    setUploading(false)
    queryClient.invalidateQueries({ queryKey: ['vendor-job-photos', job.field_job_id] })
    if (errMsg) toast.error(`Upload failed: ${errMsg}`)
    else if (okCount > 0) toast.success(`${okCount} photo${okCount === 1 ? '' : 's'} uploaded`)
  }

  if (isLoading) return <div style={{ height: 200, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }} />

  if (!job) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Link to="/vendor/jobs" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-tertiary)', textDecoration: 'none' }}>
          <ArrowLeft style={{ width: 12, height: 12 }} /> Back to jobs
        </Link>
        <div style={{ padding: 24, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, color: 'var(--ink-secondary)', fontSize: 14, textAlign: 'center' }}>
          Not found.
        </div>
      </div>
    )
  }

  // Source branch — self-entered jobs have no assignment lifecycle, no
  // photos, no work order. Render an isolated SelfJobView that handles
  // edit / delete / mark-complete. Assigned jobs fall through to the
  // existing render (byte-unchanged).
  if (job.source === 'self_entered') {
    return <SelfJobView job={job} />
  }

  const budget = formatBudget(job.budget_offered)
  const addrLine = [job.address_line1, job.address_city, job.address_state, job.address_zip].filter(Boolean).join(', ')
  const sched = job.scheduled_date ? formatRelativeDate(job.scheduled_date) : null
  const window = (job.scheduled_start && job.scheduled_end) ? `${job.scheduled_start}–${job.scheduled_end}` : job.scheduled_start
  const isPending = job.assignment_status === 'pending'
  const isAccepted = job.assignment_status === 'accepted'
  const isCompleted = !!job.completed_at
  // Switched to the explicit V2b column for consistency with the
  // staff JobPhotoStrip + portal PortalPhotoStrip groupings — same
  // attribution test everywhere. Legacy rows (both columns NULL, pre-
  // V2b) now fall in the staff group, which matches their origin.
  const staffPhotos = photos.filter(p => p.uploaded_by_vendor_user_id == null)
  const vendorPhotos = photos.filter(p => p.uploaded_by_vendor_user_id != null)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Link to="/vendor/jobs" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-tertiary)', textDecoration: 'none', alignSelf: 'flex-start' }}>
        <ArrowLeft style={{ width: 12, height: 12 }} /> Back to jobs
      </Link>

      {/* Header card */}
      <motion.section
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        style={{ background: 'var(--surface)', border: `1px solid ${isPending ? 'var(--accent-border)' : 'var(--border-subtle)'}`, borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>{job.title ?? 'Service visit'}</h1>
            {(job.work_order_number || job.purchase_order_number) && (
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--ink-tertiary)' }}>
                {job.work_order_number ? `WO ${job.work_order_number}` : ''}
                {job.work_order_number && job.purchase_order_number ? ' · ' : ''}
                {job.purchase_order_number ? `PO ${job.purchase_order_number}` : ''}
              </p>
            )}
          </div>
          <StatusPill status={job.assignment_status} completed={isCompleted} />
        </div>

        {budget && (
          <div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 999 }}>
            <DollarSign style={{ width: 13, height: 13, color: 'var(--accent-text)' }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-text)' }}>{budget}</span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sched && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--ink-secondary)' }}>
              <CalendarClock style={{ width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
              {sched}{window ? ` · ${window}` : ''}
            </div>
          )}
          {addrLine && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--ink-secondary)' }}>
              <MapPin style={{ width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
              {directionsUrl
                ? <a href={directionsUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>{addrLine}</a>
                : addrLine}
            </div>
          )}
        </div>

        {job.job_notes && (
          <div style={{ padding: '10px 12px', background: 'var(--hover-bg)', borderRadius: 8 }}>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Job details</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-primary)', whiteSpace: 'pre-wrap' }}>{job.job_notes}</p>
          </div>
        )}

        <button type="button" onClick={downloadWorkOrder}
          style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 7, padding: '8px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          <Download style={{ width: 13, height: 13 }} />
          Download work order PDF
        </button>

        {isPending && (
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button type="button" disabled={respond.isPending}
              onClick={() => respond.mutate({ op: 'accept_session' })}
              style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px', background: 'var(--accent)', border: 'none', borderRadius: 9, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              <CheckCircle2 style={{ width: 14, height: 14 }} /> Accept
            </button>
            <button type="button" disabled={respond.isPending}
              onClick={() => { setDeclineOpen(true); setDeclineReason('') }}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px 16px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <XCircle style={{ width: 14, height: 14 }} /> Decline
            </button>
          </div>
        )}
      </motion.section>

      {/* Staff photos (scope) — tap opens the shared in-place Lightbox.
          Indices in the combined [staff, vendor] array so prev/next walk
          both groups continuously. */}
      {staffPhotos.length > 0 && (
        <section style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 16 }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Job scope</h2>
          <PhotoStrip photos={staffPhotos} onPhotoClick={(i) => setLightboxIndex(i)} />
        </section>
      )}

      {/* Job status — lifecycle (accepted only) */}
      {isAccepted && (
        <section style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Job status</h2>
            {job.latest_event_type && (
              <LifecycleChip type={job.latest_event_type} at={job.latest_event_at ?? undefined} />
            )}
          </div>

          {/* Lifecycle action buttons — large, thumb-friendly, mobile-first.
              Five rows on phone, two-column at wider widths via the flex
              wrap below. Disabled when the latest event is already
              `completed` (the assignment is in a terminal vendor state). */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            <LifecycleButton evt="en_route" disabled={isCompleted || logEvent.isPending} onClick={() => logEvent.mutate({ type: 'en_route' })} />
            <LifecycleButton evt="on_site"  disabled={isCompleted || logEvent.isPending} onClick={() => logEvent.mutate({ type: 'on_site' })} />
            <LifecycleButton evt="delayed"  disabled={isCompleted || logEvent.isPending} onClick={() => { setDelayNote(''); setDelayOpen(true) }} />
            <LifecycleButton evt="return_visit_scheduled" disabled={isCompleted || logEvent.isPending} onClick={() => { setReturnDate(''); setReturnNote(''); setReturnOpen(true) }} />
            <LifecycleButton evt="completed" disabled={isCompleted || logEvent.isPending} onClick={() => setConfirmCompleteOpen(true)} />
          </div>

          {isCompleted && (
            <div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 7, padding: '8px 14px', background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', borderRadius: 8, color: 'var(--positive)', fontSize: 12, fontWeight: 700 }}>
              <CheckCircle2 style={{ width: 13, height: 13 }} />
              Marked complete · {formatRelativeDate(job.completed_at!)}
            </div>
          )}

          {/* Proof of work — photos */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Proof of work</p>
            {vendorPhotos.length > 0 && (
              <PhotoStrip photos={vendorPhotos}
                onPhotoClick={(i) => setLightboxIndex(staffPhotos.length + i)} />
            )}
            <label htmlFor="vendor-photo-upload"
              style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 7, padding: '10px 16px', background: 'var(--hover-bg)', border: '1px dashed var(--border-default)', borderRadius: 9, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer' }}>
              {uploading ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Camera style={{ width: 14, height: 14 }} />}
              {uploading ? 'Uploading…' : 'Add photos'}
            </label>
            <input id="vendor-photo-upload" type="file" accept="image/*" capture="environment" multiple
              disabled={uploading}
              onChange={e => e.target.files && onUpload(e.target.files)}
              style={{ display: 'none' }} />
            <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-tertiary)' }}>
              On mobile, the button opens your camera. iPhone photos may upload as HEIC; staff sees them on the job record either way.
            </p>
          </div>

          {/* Timeline — newest first */}
          {events.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
              <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Timeline</p>
              <Timeline events={events} ownVendorUserId={vendorUser?.id ?? null} />
            </div>
          )}
        </section>
      )}

      {/* Decline modal */}
      {declineOpen && (
        <div onClick={() => { if (!respond.isPending) setDeclineOpen(false) }}
          style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16, zIndex: 50 }}>
          <motion.div onClick={e => e.stopPropagation()}
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.22 }}
            style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-popover-high)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle style={{ width: 16, height: 16, color: 'var(--warning)' }} />
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Decline this job?</h2>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-secondary)' }}>Optional — let the operator know why.</p>
            <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={4} maxLength={DECLINE_MAX}
              placeholder="Reason (optional)"
              style={{ width: '100%', padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDeclineOpen(false)} disabled={respond.isPending}
                style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button type="button" disabled={respond.isPending}
                onClick={() => respond.mutate({ op: 'decline_session', reason: declineReason.trim() || null })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--danger)', border: 'none', borderRadius: 8, color: 'var(--ink-inverse)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {respond.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
                Decline job
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delay modal — note required */}
      {delayOpen && (
        <div onClick={() => { if (!logEvent.isPending) setDelayOpen(false) }}
          style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16, zIndex: 50 }}>
          <motion.div onClick={e => e.stopPropagation()}
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.22 }}
            style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-popover-high)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TimerReset style={{ width: 16, height: 16, color: 'var(--warning)' }} />
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Report a delay</h2>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-secondary)' }}>What's the holdup? Staff sees this in their bell and on the job record.</p>
            <textarea value={delayNote} onChange={e => setDelayNote(e.target.value)} rows={4} maxLength={NOTE_MAX} autoFocus
              placeholder="Stuck in traffic, parts not in yet, etc."
              style={{ width: '100%', padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDelayOpen(false)} disabled={logEvent.isPending}
                style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button type="button"
                disabled={logEvent.isPending || delayNote.trim().length === 0}
                onClick={() => { logEvent.mutate({ type: 'delayed', note: delayNote.trim() }); setDelayOpen(false) }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--warning)', border: 'none', borderRadius: 8, color: 'var(--ink-inverse)', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (logEvent.isPending || delayNote.trim().length === 0) ? 0.5 : 1 }}>
                {logEvent.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
                Report delay
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Return visit modal — date required */}
      {returnOpen && (
        <div onClick={() => { if (!logEvent.isPending) setReturnOpen(false) }}
          style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16, zIndex: 50 }}>
          <motion.div onClick={e => e.stopPropagation()}
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.22 }}
            style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-popover-high)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <RotateCcw style={{ width: 16, height: 16, color: 'var(--accent-text)' }} />
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Schedule a return visit</h2>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-secondary)' }}>Date is required. Add a quick note if helpful.</p>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Return date</span>
              <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} autoFocus
                style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
            </label>
            <textarea value={returnNote} onChange={e => setReturnNote(e.target.value)} rows={3} maxLength={NOTE_MAX}
              placeholder="Optional — what you need on the next visit"
              style={{ width: '100%', padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setReturnOpen(false)} disabled={logEvent.isPending}
                style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button type="button"
                disabled={logEvent.isPending || !returnDate}
                onClick={() => { logEvent.mutate({ type: 'return_visit_scheduled', date: returnDate, note: returnNote.trim() || undefined }); setReturnOpen(false) }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (logEvent.isPending || !returnDate) ? 0.5 : 1 }}>
                {logEvent.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
                Log return visit
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Confirm complete modal */}
      {confirmCompleteOpen && (
        <div onClick={() => { if (!logEvent.isPending) setConfirmCompleteOpen(false) }}
          style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16, zIndex: 50 }}>
          <motion.div onClick={e => e.stopPropagation()}
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.22 }}
            style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-popover-high)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle2 style={{ width: 16, height: 16, color: 'var(--positive)' }} />
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Mark this job complete?</h2>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-secondary)' }}>
              The operator will be notified. You can still add photos afterward.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmCompleteOpen(false)} disabled={logEvent.isPending}
                style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Not yet</button>
              <button type="button" disabled={logEvent.isPending}
                onClick={() => { logEvent.mutate({ type: 'completed' }); setConfirmCompleteOpen(false) }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--positive)', border: 'none', borderRadius: 8, color: 'var(--ink-inverse)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {logEvent.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
                Mark complete
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* In-place photo lightbox. Combined array so prev/next walk
          continuously across the Job scope (staff) and Proof of work
          (vendor) groups. signedUrlQueryKey matches the Thumbnail's
          query key above — the lightbox opens on a cache hit, no
          new fetch. Vendor view omits the optional chrome (no delete,
          no Vendor attribution pill). */}
      {lightboxIndex != null && (() => {
        const all = [...staffPhotos, ...vendorPhotos]
        if (!all[lightboxIndex]) return null
        return (
          <Lightbox
            photos={all}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onPrev={() => setLightboxIndex(idx => idx == null ? null : (idx - 1 + all.length) % all.length)}
            onNext={() => setLightboxIndex(idx => idx == null ? null : (idx + 1) % all.length)}
            signedUrlQueryKey="vendor-photo-url"
          />
        )
      })()}
    </div>
  )
}

function LifecycleButton({ evt, disabled, onClick }: { evt: LifecycleEvent; disabled: boolean; onClick: () => void }) {
  const c = EVENT_COPY[evt]
  const Icon = c.icon
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, padding: '12px 14px', background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, color: c.color, fontSize: 13, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, textAlign: 'left' }}>
      <Icon style={{ width: 16, height: 16 }} />
      {c.label}
    </button>
  )
}

export function LifecycleChip({ type, at }: { type: LifecycleEvent; at?: string }) {
  const c = EVENT_COPY[type]
  const Icon = c.icon
  return (
    <span title={at ? `Updated ${formatRelativeDate(at)}` : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', background: c.bg, border: `1px solid ${c.border}`, color: c.color, borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
      <Icon style={{ width: 11, height: 11 }} />
      {c.chipLabel}
    </span>
  )
}

function Timeline({ events, ownVendorUserId }: { events: AssignmentEvent[]; ownVendorUserId: string | null }) {
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {events.map(ev => {
        const c = EVENT_COPY[ev.event_type]
        const isOwn = ev.vendor_user_id && ev.vendor_user_id === ownVendorUserId
        return (
          <li key={ev.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: c.bg, border: `1px solid ${c.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <c.icon style={{ width: 13, height: 13, color: c.color }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>
                {c.chipLabel}
                {isOwn && <span style={{ fontSize: 10, marginLeft: 6, color: 'var(--ink-tertiary)', fontWeight: 500 }}>(you)</span>}
              </p>
              {ev.scheduled_return_date && (
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-secondary)' }}>
                  Return on {ev.scheduled_return_date}
                </p>
              )}
              {ev.note && (
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-secondary)', whiteSpace: 'pre-wrap' }}>{ev.note}</p>
              )}
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(ev.created_at)}</p>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function StatusPill({ status, completed }: { status: VendorJobRow['assignment_status']; completed: boolean }) {
  const cfg: Record<string, { label: string; bg: string; color: string; border: string }> = {
    pending:   { label: 'Pending',  bg: 'var(--accent-dim)',  color: 'var(--accent-text)',  border: 'var(--accent-border)' },
    accepted:  { label: completed ? 'Complete' : 'Accepted', bg: 'var(--positive-dim)', color: 'var(--positive)', border: 'var(--positive-border)' },
    declined:  { label: 'Declined', bg: 'var(--hover-bg)',    color: 'var(--ink-tertiary)', border: 'var(--border-default)' },
    expired:   { label: 'Expired',  bg: 'var(--hover-bg)',    color: 'var(--ink-tertiary)', border: 'var(--border-default)' },
    cancelled: { label: 'Cancelled', bg: 'var(--hover-bg)',   color: 'var(--ink-tertiary)', border: 'var(--border-default)' },
  }
  const c = (status ? cfg[status] : undefined) ?? cfg.declined
  return (
    <span style={{ padding: '4px 10px', borderRadius: 999, background: c.bg, border: `1px solid ${c.border}`, color: c.color, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {c.label}
    </span>
  )
}

// Self-entered jobs use job_status (scheduled / complete / cancelled), NOT
// assignment_status (NULL on this branch). Separate pill so each source
// renders the status concept that actually exists for it.
function SelfJobStatusPill({ status }: { status: string | null }) {
  const cfg: Record<string, { label: string; bg: string; color: string; border: string }> = {
    scheduled: { label: 'Scheduled', bg: 'var(--accent-dim)',  color: 'var(--accent-text)',  border: 'var(--accent-border)' },
    complete:  { label: 'Complete',  bg: 'var(--positive-dim)', color: 'var(--positive)',    border: 'var(--positive-border)' },
    cancelled: { label: 'Cancelled', bg: 'var(--hover-bg)',     color: 'var(--ink-tertiary)', border: 'var(--border-default)' },
  }
  const c = (status ? cfg[status] : undefined) ?? cfg.scheduled
  return (
    <span style={{ padding: '4px 10px', borderRadius: 999, background: c.bg, border: `1px solid ${c.border}`, color: c.color, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {c.label}
    </span>
  )
}

function PhotoStrip({
  photos, onPhotoClick,
}: {
  photos: JobPhoto[]
  onPhotoClick: (indexInGroup: number) => void
}) {
  if (photos.length === 0) {
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-tertiary)' }}>
        <ImageIcon style={{ width: 12, height: 12 }} /> No photos yet
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
      {photos.map((p, i) => <Thumbnail key={p.id} photo={p} onClick={() => onPhotoClick(i)} />)}
    </div>
  )
}

// <button onClick> — NOT <a target="_blank">. Mobile-Safari fix: tapping
// a thumbnail must open the in-place Lightbox, never tab-jack the user
// out of the app. Same signed-URL query key as the lightbox uses
// internally (passed via signedUrlQueryKey prop) so the cache hits and
// the lightbox opens with zero new fetch.
function Thumbnail({ photo, onClick }: { photo: JobPhoto; onClick: () => void }) {
  const { data: url } = useQuery({
    queryKey: ['vendor-photo-url', photo.storage_path],
    queryFn: async () => {
      const { url } = await getJobPhotoSignedUrl(photo.storage_path, 300)
      return url
    },
    staleTime: 290_000,
    gcTime: 290_000,
  })
  return (
    <button type="button" onClick={onClick}
      aria-label={photo.caption ?? 'Open photo'}
      style={{ width: 96, height: 96, flexShrink: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'var(--hover-bg)', padding: 0, cursor: 'pointer', display: 'block' }}>
      {url && <img src={url} alt={photo.caption ?? ''} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
    </button>
  )
}

// ─── SelfJobView ───────────────────────────────────────────────────────────
//
// Self-entered job surface. Renders for a vendor_jobs row where source =
// 'self_entered'. Isolated from the assigned render path — no assignment
// lifecycle (no accept/decline, no en_route/on_site/delayed/return events,
// no work-order download, no photos). Just: title/sched/status header,
// Edit + Delete affordances, and a Mark Complete shortcut.
//
// All writes are direct table mutations against vendor_self_jobs gated by
// the Phase 1 RLS policies. No edge function — same pattern as
// VendorSettings (notification_prefs UPDATE) and VendorJobDetail's V2c
// lifecycle insert.
function SelfJobView({ job }: { job: VendorJobRow }) {
  const { assignmentId } = useParams<{ assignmentId: string }>()
  const queryClient = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const sched = job.scheduled_date ? formatRelativeDate(job.scheduled_date) : null
  const window = (job.scheduled_start && job.scheduled_end) ? `${job.scheduled_start}–${job.scheduled_end}` : (job.scheduled_start ?? null)
  const isComplete   = job.job_status === 'complete'
  const isCancelled  = job.job_status === 'cancelled'
  const isTerminal   = isComplete || isCancelled

  const markComplete = useMutation({
    mutationFn: async () => {
      if (!assignmentId) throw new Error('No id')
      const { error } = await supabase
        .from('vendor_self_jobs').update({ status: 'complete' }).eq('id', assignmentId)
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Marked complete')
      queryClient.invalidateQueries({ queryKey: ['vendor-job', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['vendor-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['vendor-history'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not update'),
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Link to="/vendor/jobs" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-tertiary)', textDecoration: 'none', alignSelf: 'flex-start' }}>
        <ArrowLeft style={{ width: 12, height: 12 }} /> Back to jobs
      </Link>

      <motion.section
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span title="Your private job — staff can't see the details"
              style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 999, fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Private job
            </span>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>{job.title ?? 'Private job'}</h1>
          </div>
          <SelfJobStatusPill status={job.job_status} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sched && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--ink-secondary)' }}>
              <CalendarClock style={{ width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
              {sched}{window ? ` · ${window}` : ''}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
          {!isTerminal && (
            <button type="button"
              disabled={markComplete.isPending}
              onClick={() => markComplete.mutate()}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', borderRadius: 9, color: 'var(--positive)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              {markComplete.isPending ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : <CheckCircle2 style={{ width: 13, height: 13 }} />}
              Mark complete
            </button>
          )}
          <button type="button" onClick={() => setEditOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Edit
          </button>
          <button type="button" onClick={() => setDeleteOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 9, color: 'var(--ink-tertiary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Delete
          </button>
        </div>
      </motion.section>

      {editOpen && <EditSelfJobModal job={job} onClose={() => setEditOpen(false)} />}
      {deleteOpen && <DeleteSelfJobModal jobId={assignmentId!} title={job.title} onClose={() => setDeleteOpen(false)} />}
    </div>
  )
}

// Edit modal — UPDATEs private_label/date/start/end/status. Status is
// included so a vendor can move scheduled → complete → cancelled in one
// place (the dedicated Mark Complete button is a convenience shortcut for
// the most common case).
const PRIVATE_LABEL_MAX_EDIT = 120

function EditSelfJobModal({ job, onClose }: { job: VendorJobRow; onClose: () => void }) {
  const queryClient = useQueryClient()
  const { assignmentId } = useParams<{ assignmentId: string }>()
  const [form, setForm] = useState({
    private_label:   job.title ?? '',
    scheduled_date:  job.scheduled_date ?? '',
    // job.scheduled_start/end on the view are HH:MI text — feed input
    // type=time directly.
    scheduled_start: job.scheduled_start ?? '',
    scheduled_end:   job.scheduled_end ?? '',
    status:          (job.job_status ?? 'scheduled') as 'scheduled' | 'complete' | 'cancelled',
  })

  const save = useMutation({
    mutationFn: async () => {
      if (!assignmentId) throw new Error('No id')
      const trimmed = form.private_label.trim()
      if (trimmed.length === 0) throw new Error('Label is required.')
      if (form.scheduled_start >= form.scheduled_end) throw new Error('End time must be after start time.')
      const { error } = await supabase
        .from('vendor_self_jobs')
        .update({
          private_label:   trimmed,
          scheduled_date:  form.scheduled_date,
          scheduled_start: form.scheduled_start,
          scheduled_end:   form.scheduled_end,
          status:          form.status,
        })
        .eq('id', assignmentId)
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Saved')
      queryClient.invalidateQueries({ queryKey: ['vendor-job', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['vendor-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['vendor-history'] })
      onClose()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not save'),
  })

  return (
    <div onClick={() => { if (!save.isPending) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16, zIndex: 50 }}>
      <motion.div onClick={e => e.stopPropagation()}
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.22 }}
        style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-popover-high)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Edit private job</h2>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Label</span>
          <input type="text" autoFocus
            value={form.private_label}
            onChange={e => setForm(f => ({ ...f, private_label: e.target.value }))}
            maxLength={PRIVATE_LABEL_MAX_EDIT}
            style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Date</span>
          <input type="date" value={form.scheduled_date}
            onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
            style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Start</span>
            <input type="time" value={form.scheduled_start}
              onChange={e => setForm(f => ({ ...f, scheduled_start: e.target.value }))}
              style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>End</span>
            <input type="time" value={form.scheduled_end}
              onChange={e => setForm(f => ({ ...f, scheduled_end: e.target.value }))}
              style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
          </label>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</span>
          <select value={form.status}
            onChange={e => setForm(f => ({ ...f, status: e.target.value as 'scheduled' | 'complete' | 'cancelled' }))}
            style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', cursor: 'pointer' }}>
            <option value="scheduled">Scheduled</option>
            <option value="complete">Complete</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={save.isPending}
            style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button type="button" disabled={save.isPending}
            onClick={() => save.mutate()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {save.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
            Save
          </button>
        </div>
      </motion.div>
    </div>
  )
}

function DeleteSelfJobModal({ jobId, title, onClose }: { jobId: string; title: string | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const del = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('vendor_self_jobs').delete().eq('id', jobId)
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Deleted')
      queryClient.invalidateQueries({ queryKey: ['vendor-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['vendor-history'] })
      navigate('/vendor/jobs', { replace: true })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not delete'),
  })

  return (
    <div onClick={() => { if (!del.isPending) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16, zIndex: 50 }}>
      <motion.div onClick={e => e.stopPropagation()}
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.22 }}
        style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-popover-high)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle style={{ width: 16, height: 16, color: 'var(--danger)' }} />
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Delete this job?</h2>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-secondary)' }}>
          {title ? `"${title}" will be removed permanently.` : 'This job will be removed permanently.'} The time slot also frees up on the busy signal staff sees.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={del.isPending}
            style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button type="button" disabled={del.isPending}
            onClick={() => del.mutate()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--danger)', border: 'none', borderRadius: 8, color: 'var(--ink-inverse)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {del.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
            Delete job
          </button>
        </div>
      </motion.div>
    </div>
  )
}
